import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  #optimisticPending = false;
  #optimisticRevision = 0;

  /** @type {number} */
  get currentCartCount() {
    const value = Number(this.dataset.cartCount ?? this.refs.cartBubbleCount.textContent);
    return Number.isFinite(value) ? value : 0;
  }

  set currentCartCount(value) {
    this.dataset.cartCount = String(value);
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';

    const spokenCount = this.refs.cartBubbleText.querySelector('.visually-hidden');
    if (spokenCount) spokenCount.textContent = spokenCount.textContent.replace(/:\s*\d+\s*$/, `: ${value}`);
    const trigger = this.closest('button[aria-label], a[aria-label]');
    if (trigger) trigger.setAttribute('aria-label', trigger.getAttribute('aria-label').replace(/:\s*\d+\s*$/, `: ${value}`));
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('ps:cart-count-adjust', this.onOptimisticAdjust);
    document.addEventListener('ps:cart-sync-idle', this.onCartSyncIdle);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('ps:cart-count-adjust', this.onOptimisticAdjust);
    document.removeEventListener('ps:cart-sync-idle', this.onCartSyncIdle);
  }

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    if (this.#optimisticPending) return;
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /** Count each accepted tap now, even while the Ajax cart request is in flight. */
  onOptimisticAdjust = (event) => {
    const delta = Number(event.detail?.delta);
    if (!Number.isFinite(delta) || delta === 0) return;
    this.#optimisticPending = true;
    this.#optimisticRevision += 1;
    this.renderCartBubble(Math.max(0, this.currentCartCount + delta), false, false);
  };

  /** Reconcile after all queued adds and quantity changes have settled. */
  onCartSyncIdle = async () => {
    if (!this.#optimisticPending) return;
    const revision = this.#optimisticRevision;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Could not refresh cart count');
      const cart = await response.json();
      if (revision !== this.#optimisticRevision || window.psCartSync?.pending) return;
      this.#optimisticPending = false;
      this.renderCartBubble(cart.item_count, false, false);
      document.dispatchEvent(new CustomEvent('ps:cart-count-confirmed', { detail: { count: cart.item_count } }));
    } catch (_) {
      // A later cart update or page load can retry the authoritative count.
      if (revision === this.#optimisticRevision) this.#optimisticPending = false;
    }
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    // If the cart update is coming from the product form, we add to the current cart count, otherwise we set the new cart count

    this.refs.cartBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.currentCartCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;

    this.classList.toggle('header-actions__cart-icon--has-cart', itemCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
